import React from "react";
import { Button, Dialog, DialogActions, DialogContent, Stack, Typography } from "@mui/material";
import { CONFIRMATION_TYPES, DIALOG_EVENT } from "Common/Constants";
import { WarningRounded } from "@mui/icons-material";
import { IConfirmationContent } from "Common/Interfaces";
import { ActivateOutlined, BinOutlined, DeactivateOutlined } from "Icons/Icons";

interface IConfirmationDialogProps {
  open: boolean;
  dialogEvent: (event: DIALOG_EVENT) => void;
  confirmationContent: IConfirmationContent | null;
}

const generateIconAndStyles = (event: CONFIRMATION_TYPES): React.JSX.Element | null => {
  switch (event) {
    case CONFIRMATION_TYPES.DELETE:
      return <BinOutlined color="error" sx={{ fontSize: 32 }} />;
    case CONFIRMATION_TYPES.DISABLE:
      return <DeactivateOutlined color="error" sx={{ fontSize: 32 }} />;
    case CONFIRMATION_TYPES.RESET:
      return <WarningRounded color="warning" sx={{ fontSize: 32 }} />;
    case CONFIRMATION_TYPES.ENABLE:
      return <ActivateOutlined color="success" sx={{ fontSize: 32 }} />;
    case CONFIRMATION_TYPES.CANCEL:
      return null;
    default:
      return <WarningRounded color="info" sx={{ fontSize: 32 }} />;
  }
};

export const ConfirmationDialog = React.memo((props: IConfirmationDialogProps) => {
  const { open, dialogEvent, confirmationContent } = props;

  const icon = generateIconAndStyles(confirmationContent?.event ?? CONFIRMATION_TYPES.DELETE);
  return (
    <Dialog
      aria-labelledby="confirm-dialog-title"
      open={open}
      maxWidth={confirmationContent?.dialogWidth ?? "xs"}
      fullWidth
      slotProps={{
        paper: {
          variant: "popup",
          sx: {
            padding: 0,
          },
        },
      }}
    >
      {confirmationContent && (
        <>
          <DialogContent
            sx={{
              paddingTop: 3,
              paadingBottom: 2,
            }}
          >
            <Stack sx={{ alignItems: "center" }}>
              {icon}
              <Stack
                spacing={2}
                sx={{
                  marginTop: icon ? 2 : 0,
                  width: "100%",
                }}
              >
                <Typography
                  id="confirm-dialog-title"
                  variant="h5"
                  sx={{
                    fontWeight: "fontWeightSemiBold",
                    textAlign: "center",
                    color: "primary.main",
                  }}
                >
                  {confirmationContent.title}
                </Typography>
                <>{confirmationContent.message}</>
              </Stack>
            </Stack>
          </DialogContent>
          <DialogActions
            sx={{
              borderTop: "none",
              justifyContent: "center",
              padding: 3,
            }}
          >
            {!confirmationContent.hideCancelButton && (
              <Button
                id="cancel-button"
                onClick={() => dialogEvent(DIALOG_EVENT.CANCEL)}
                variant="text"
                disabled={confirmationContent.disabledCancelButton}
                sx={{ px: 4 }}
              >
                {confirmationContent.cancelButtonText}
              </Button>
            )}

            <span>
              <Button
                id="confirm-button"
                onClick={() => dialogEvent(DIALOG_EVENT.CONFIRM)}
                color={"primary"}
                variant="contained"
                autoFocus
                loading={confirmationContent.isLoadingConfirmButton}
                loadingPosition="start"
                disabled={confirmationContent.disabledConfirmButton}
                sx={{ px: 4 }}
              >
                {confirmationContent.confirmButtonText}
              </Button>
            </span>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
});
